<template>
  <div>
    <div
      id="geofence_group_map_internal"
      class="map" />
    <div
      id="inventory_cluster_popup_content"
      style="display: none">
      <div class="site-popup-content">
        <div v-if="currentClusterInventory">
          <div class="title">
            <strong
              >{{ currentClusterInventory.length }} {{ $t('names.screen', currentClusterInventory.length) }}</strong
            >
          </div>
          <hr />
          <div class="inventory-table-container">
            <table>
              <thead>
                <tr>
                  <th>{{ capitalize($t('names.unitID', { n: 1 })) }}</th>
                  <th>{{ capitalize($t('names.name', { n: 1 })) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="inventory in currentClusterInventory"
                  :key="inventory.externalID">
                  <td :title="inventory.externalID">{{ inventory.externalID }}</td>
                  <td>{{ inventory.name }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  import MapboxDraw from '@mapbox/mapbox-gl-draw';
  import turfBuffer from '@turf/buffer';
  import turfCentroid from '@turf/centroid';
  import * as turf from '@turf/helpers';
  import turfUnion from '@turf/union';
  import { DateTime } from 'luxon';
  import mapboxgl, { LngLat } from 'mapbox-gl';
  import Supercluster from 'supercluster';
  import { capitalize } from 'vue';
  import { mapActions } from 'vuex';

  import Colors from '@/Colors';
  import { getContainerHeight, getContainerWidth, getDistanceFromLatLonInKm } from '@/globals';
  import UnitClustersService from '@/org-app/components/geofenceGroups/UnitClustersService';

  import GeofenceContextMenuBuilder from './geofenceContextMenu/GeofenceContextMenuBuilder';
  import MapPointControl from './MapPointControl';
  import MapPolygonControl from './MapPolygonControl';
  import MapStyleControl from './MapStyleControl';

  export default {
    props: {
      isEditable: {
        type: Boolean,
        default: false,
      },
      geofenceGroup: {
        type: Object,
        default: null,
      },
      activeGeofence: {
        type: Object,
        default: null,
      },
      unitsInGeofenceGroup: {
        type: Array,
      },
    },
    data() {
      return {
        map: null,
        mode: null,
        mapboxDraw: null,
        mapStyle: 'mapbox://styles/mapbox/light-v9',
        lastClicked3DBuildingEvent: null,
        lastClickedOnPolygonAt: null,
        lastClickedPointEvent: null,
        lastMapboxDrawSelectionChangeTimestamp: DateTime.local(),
        superC: null,
        pulseMarkerInitialRadius: 8,
        circleRadiusDistance: 25,
        circleRadiusDistanceUnit: 'm',
        clusterRadius: 40,
        currentSitePopup: null,
        currentClusterInventory: [],
        /**
         * @type {GeofenceContextMenuBuilder}
         */
        siteContextMenu: null,
        unitClustersService: null,
      };
    },
    methods: {
      capitalize,
      ...mapActions([
        'updateGeofenceIsPoint',
        'updateGeofenceExtrusion',
        'updateGeofenceMapboxDrawID',
        'updateGeofenceLat',
        'updateGeofenceLng',
        'updateGeofenceGeoJson',
      ]),
      initMap() {
        const mapOptions = {
          container: 'geofence_group_map_internal',
          style: this.mapStyle,
          minzoom: 1.3,
          zoom: 1,
        };
        const map = new mapboxgl.Map(mapOptions);
        this.map = map;
        this.unitClustersService = new UnitClustersService(this.map);

        map.on('load', () => {
          this.createMapLayers();

          this.unitClustersService.init(!this.isEditable, false, 'place_points_layer');

          //
          // Handle clicking on geofence points, displays a popup and select the geofence in the list.
          //
          this.map.on('click', 'place_points_layer', (e) => {
            const { id } = e.features[0].properties;
            // this.activateGeofence(id);
            this.$emit('pointClicked', id);
          });

          //
          // Handle clicking on geofence points, displays a popup and select the geofence in the list.
          //
          this.map.on('contextmenu', 'place_polygons_fill_layer', (e) => {
            if (!this.isEditable) {
              return;
            }
            const { id } = e.features[0].properties;
            if (id == null) {
              return;
            }
            const targetedGeofence = this.geofenceGroup?.geofences?.find((geofence) => geofence.id === id);
            if (targetedGeofence == null) {
              return;
            }
            this.$emit('highlightGeofence', id);
            // If a context menu is already open, close it
            this.siteContextMenu?.destroy();
            const deleteGeofence = () => {
              this.mapboxDraw.delete(id);
              this.$emit('deleteGeofence', targetedGeofence);
              this.siteContextMenu?.destroy();
            };
            this.siteContextMenu = new GeofenceContextMenuBuilder(this.map, { deleteGeofence });
            const lngLat = new LngLat(targetedGeofence.lng, targetedGeofence.lat);
            this.siteContextMenu.create({
              coordinates: lngLat,
              className: 'site-context-menu',
              anchorId: `geofence-site-context-menu-${id}`,
            });
          });

          if (this.isEditable) {
            //
            // Handle clicking on geofence polygons, update a timestamp use to make sure
            // clicking on a 3d building already added does not add it again.
            //
            this.map.on('click', 'place_polygons_fill_layer', (e) => {
              this.lastClicked3DBuildingEvent = null;

              if (e.features[0].layer.id !== 'place_polygons_fill_layer') {
                return;
              }

              const { id } = e.features[0].properties;
              this.lastClickedPointEvent = e;
              this.$emit('pointClicked', id, false, false);
              this.lastClickedOnPolygonAt = DateTime.local();
            });

            this.map.on('dblclick', 'place_polygons_fill_layer', (e) => {
              const geofences = this.map.queryRenderedFeatures([e.point.x, e.point.y], {
                layers: ['place_polygons_fill_layer'],
              });
              const { id } = geofences[0].properties;
              this.$emit('pointClicked', id, false, false);
              const geofence = this.activeGeofence;
              if (geofence.isPoint) {
                const buildings = this.map.queryRenderedFeatures([e.point.x, e.point.y], { layers: ['3d-buildings'] });
                this.geofenceFromBuilding(buildings, true);
              }
            });

            //
            // Handles clicking on 3d building on the map. Will create geofence from the building model.
            //
            this.map.on('click', '3d-buildings', (e) => {
              const timeSinceLastMapdrawSelectionchange = DateTime.local().diff(
                this.lastMapboxDrawSelectionChangeTimestamp,
              );
              if (timeSinceLastMapdrawSelectionchange < 100 || this.mode === 'create-point') {
                return;
              }
              this.lastClicked3DBuildingEvent = e;
              this.geofenceFromBuilding(e.features);
            });
          }

          this.map.on('click', 'inventory_unclustered_point', (e) => {
            const inventory = e.features[0];
            this.showClusterPopupAtLocation(inventory.geometry.coordinates[0], inventory.geometry.coordinates[1], [
              inventory,
            ]);
          });

          this.map.on('click', 'inventory_clusters', (e) => {
            const latLng = e.features[0].geometry.coordinates;
            const currentZoom = parseInt(this.map.getZoom(), 10);
            const thisCluster = this.superC.getClusters(
              [e.lngLat.lng - 1, e.lngLat.lat - 1, e.lngLat.lng + 1, e.lngLat.lat + 1],
              currentZoom,
            );
            let clusterId;
            for (let i = 0; i < thisCluster.length; i += 1) {
              if (thisCluster[i].properties) {
                if (thisCluster[i].properties.cluster) {
                  clusterId = thisCluster[i].properties.cluster_id;
                }
              }
            }

            if (clusterId || clusterId === 0) {
              const zoom = this.superC.getClusterExpansionZoom(clusterId, currentZoom) + 1;
              this.map.flyTo({ center: latLng, zoom, speed: 1.5 });
            } else {
              const inventoryCluster = e.features[0];
              this.map
                .getSource('inventory_source')
                .getClusterLeaves(inventoryCluster.properties.cluster_id, Infinity, 0, (error, features) => {
                  this.showClusterPopupAtLocation(
                    inventoryCluster.geometry.coordinates[0],
                    inventoryCluster.geometry.coordinates[1],
                    features,
                  );
                });
            }
          });

          this.mapNavigationControl = new mapboxgl.NavigationControl();
          this.mapFullscreenControl = new mapboxgl.FullscreenControl();
          this.mapStyleControl = new MapStyleControl();
          this.mapStyleControl.clickHandler = (isSatellite) => {
            if (isSatellite) {
              this.changeMapStyleToSatellite();
            } else {
              this.changeMapStyleToLight();
            }
          };
          if (this.isEditable) {
            this.mapPointControl = new MapPointControl();
            this.mapPointControl.clickHandler = () => {
              this.mode = 'create-point';
              this.map.getCanvas().style.cursor = 'crosshair';
              this.mapboxDraw.changeMode('simple_select');
              this.mapPolygonControl.deactivate();
            };
            this.mapPolygonControl = new MapPolygonControl();
            this.mapPolygonControl.clickHandler = () => {
              this.mapboxDraw.changeMode('draw_polygon');
              this.mode = null;
              this.mapPointControl.deactivate();
            };
          }
          this.showMapControls(this.map);
          this.map.boxZoom.enable();

          if (this.isEditable) {
            if (this.mapboxDraw === null) {
              this.mapboxDraw = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                  trash: true,
                  fullscreen: false,
                },
                clickBuffer: 4,
              });

              this.map.on('draw.create', (e) => {
                this.handlePolygonCreation(e);
              });

              this.map.on('draw.delete', (e) => {
                this.handlePolygonDeletion(e);
              });

              this.map.on('draw.update', (e) => {
                this.handlePolygonUpdate(e);
              });

              this.map.on('draw.selectionchange', () => {
                this.lastMapboxDrawSelectionChangeTimestamp = DateTime.local();
              });
            }

            this.map.addControl(this.mapboxDraw);

            //
            // For some reason MapboxDraw does not seem active until you click on the polygon button,
            // the following will force it to become active (blue outlines on polygons).
            //
            setTimeout(() => {
              this.mapboxDraw.changeMode('draw_polygon');
              setTimeout(() => {
                this.mapboxDraw.changeMode('simple_select');
              }, 100);
            }, 1000);
          }
          this.$emit('addGeofences', this.geofenceGroup.geofences);
        });

        map.on('click', (e) => {
          if (this.isDrawingPolygon) {
            this.addPointToPolygonBeingDrawn(e);
          } else if (this.activeGeofence !== null) {
            this.$emit('clearActiveGeofence');
          } else if (this.mode === 'create-point') {
            this.mapPointControl.deactivate();
            this.isGeofenceFromCoordinateCreationModalVisible = true;
            this.lastPointCoordinate = { lng: e.lngLat.lng, lat: e.lngLat.lat };
            this.$emit('pointCreated', this.lastPointCoordinate);
            setTimeout(() => {
              this.mode = null;
              this.map.getCanvas().style.cursor = '';
            }, 200);
          }
        });

        return map;
      },
      createMapLayers() {
        if (this.mapStyle.indexOf('satellite') === -1) {
          if (this.map.getLayer('3d-buildings') == null) {
            this.map.addLayer({
              id: '3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': {
                  type: 'identity',
                  property: 'height',
                },
                'fill-extrusion-base': {
                  type: 'identity',
                  property: 'this.sites',
                },
                'fill-extrusion-opacity': 0.6,
              },
            });
          }
        }

        //
        // Pulse marker.
        //
        if (this.map.getSource('pulse_marker_source') == null) {
          this.map.addSource('pulse_marker_source', {
            type: 'geojson',
            data: {
              type: 'Point',
              coordinates: [],
              properties: {
                radius: 8,
              },
            },
          });
        }

        this.map.addLayer({
          id: 'point1',
          source: 'pulse_marker_source',
          type: 'circle',
          paint: {
            'circle-radius': this.pulseMarkerInitialRadius,
            'circle-color': '#007cbf',
          },
        });

        const geofenceLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.map.getSource('place_polygons_source') != null) {
          this.map.removeLayer('place_polygons_fill_layer');
          this.map.removeLayer('place_polygons_line_layer');
          this.map.removeSource('place_polygons_source');
        }

        this.map.addSource('place_polygons_source', geofenceLayerData);

        const geofenceCentroidLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.map.getSource('place_points_source') != null) {
          this.map.removeLayer('place_points_layer');
          this.map.removeSource('place_points_source');
        }
        this.map.addSource('place_points_source', geofenceCentroidLayerData);

        const geofencBoundingBoxLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
        };

        if (this.areGeofenceBoundingBoxesVisible) {
          if (this.map.getSource('place_bbox_source') != null) {
            this.map.removeLayer('place_polygons_bbox_layer');
            this.map.removeSource('place_bbox_source');
          }
          this.map.addSource('place_bbox_source', geofencBoundingBoxLayerData);

          this.map.addLayer({
            id: 'place_polygons_bbox_layer',
            type: 'line',
            source: 'place_bbox_source',
            paint: {
              'line-color': Colors.secondary,
            },
          });
        }

        this.map.addLayer({
          id: 'place_polygons_fill_layer',
          type: 'fill-extrusion',
          source: 'place_polygons_source',
          paint: {
            'fill-extrusion-opacity': 0.3,
            'fill-extrusion-height': {
              property: 'extrusion',
              type: 'identity',
            },
            'fill-extrusion-base': 0,
            'fill-extrusion-color': Colors.geofences,
          },
        });

        this.map.addLayer({
          id: 'place_polygons_line_layer',
          type: 'line',
          source: 'place_polygons_source',
          paint: {
            'line-color': Colors.geofences,
          },
        });

        this.map.addLayer({
          id: 'place_points_layer',
          type: 'circle',
          source: 'place_points_source',
          paint: {
            'circle-color': Colors.geofences,
            'circle-radius': 4,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });

        const sitesLayerData = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [],
          },
          cluster: true,
          maxzoom: 1000, // Max zoom points on, set very high. Allows us to zoom in on the map without breaking clusters
          clusterMaxZoom: 1000, // Max zoom to cluster points on, set very high
          clusterRadius: this.clusterRadius, // Radius of each cluster when clustering points (defaults to 50)
        };

        if (this.map.getSource('inventory_source') != null) {
          this.map.removeLayer('inventory_clusters');
          this.map.removeLayer('inventory_cluster_count');
          this.map.removeLayer('inventory_unclustered_point');
          this.map.removeSource('inventory_source');
        }

        this.map.addSource('inventory_source', sitesLayerData);

        this.map.addLayer({
          id: 'inventory_clusters',
          type: 'circle',
          source: 'inventory_source',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': {
              property: 'point_count',
              type: 'interval',
              stops: [
                [0, Colors.secondary],
                [100, Colors.secondary],
                [750, Colors.secondary],
              ],
            },
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': 2,
            'circle-stroke-opacity': 1,
            'circle-radius': {
              property: 'point_count',
              type: 'interval',
              stops: [
                [0, 20],
                [100, 30],
                [750, 40],
              ],
            },
          },
        });

        this.map.addLayer({
          id: 'inventory_cluster_count',
          type: 'symbol',
          source: 'inventory_source',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-size': 14,
          },
          paint: {
            'text-color': '#ffffff',
          },
        });

        this.map.addLayer({
          id: 'inventory_unclustered_point',
          type: 'circle',
          source: 'inventory_source',
          filter: ['!has', 'point_count'],
          paint: {
            'circle-color': Colors.secondary,
            'circle-radius': 8,
            'circle-stroke-width': 1,
            'circle-stroke-color': '#fff',
          },
        });
      },
      geofenceFromBuilding(features, fromDoubleClick = false) {
        if (
          !this.isConvertingPointGeofenceToPolygonGeofence &&
          !fromDoubleClick &&
          this.lastClickedOnPolygonAt !== null &&
          DateTime.local().diff(this.lastClickedOnPolygonAt) < 100
        ) {
          return;
        }

        if (features.length === 0) {
          return;
        }

        if (this.mapboxDraw.getMode() === 'draw_polygon') {
          return;
        }
        const feature = features.length === 1 ? features[0] : turfUnion(...features);

        if (feature.geometry.coordinates.length > 1) {
          // select the polygon with the most points.
          const max = Math.max(...feature.geometry.coordinates.map((x) => x[0].length));
          const coordinates = feature.geometry.coordinates.find((x) => x[0].length === max);
          feature.geometry.coordinates = [coordinates];
        }

        const bufferedFeature = turfBuffer(feature, 0.1, { units: 'meters', steps: 2 });
        const coordinates = bufferedFeature.geometry.coordinates[0];

        let prev = null;
        let currentGroup = null;
        const coordinateGroups = [];
        coordinates.forEach((x) => {
          if (prev !== null) {
            const distance = getDistanceFromLatLonInKm(x[1], x[0], prev[1], prev[0]);
            if (distance > 0.00005) {
              // 10 cm
              currentGroup = null;
            }
          }
          prev = x;
          if (currentGroup !== null) {
            currentGroup.push(x);
          } else {
            currentGroup = [x];
            coordinateGroups.push(currentGroup);
          }
        });

        const simplifiedCoordinates = coordinateGroups.map((x) => x[Math.floor(x.length / 2)]);
        simplifiedCoordinates.push(simplifiedCoordinates[0]);
        bufferedFeature.geometry.coordinates = [simplifiedCoordinates];
        //
        // Removing this block, not sure where it's used. Fix for HV-5813
        //
        // if (this.activeGeofence != null) {
        //  const geofence = this.activeGeofence;
        //  this.newlyDrawnPolygon = bufferedFeature;
        //  this.updateGeofenceMapboxDrawID({ geofence, value: this.newlyDrawnPolygon.id });
        //  const geoJSON = JSON.stringify(bufferedFeature.geometry);
        //  this.updateGeofenceGeoJson({ geofence, value: geoJSON });
        //  this.updateGeofenceIsPoint({ geofence, value: false });
        //  this.updateGeofenceExtrusion({ geofence, value: features[0].properties.height * 1.01 });
        //  this.updateGeofenceDataSources();
        // }
        this.handlePolygonCreation(
          {
            features: [bufferedFeature],
            height: features[0].properties.height * 1.01,
          },
          true,
        );
      },
      deleteGeofence(geofenceID) {
        this.mapboxDraw.delete(geofenceID);
      },
      deleteNewDrawnPolygon(geofence) {
        if (geofence.id !== null) {
          this.mapboxDraw.delete(geofence.id);
        }
      },
      showPopup(html) {
        new mapboxgl.Popup()
          .setLngLat([this.activeGeofence.lng, this.activeGeofence.lat])
          .setHTML(html)
          .addTo(this.map);
      },
      showMapControls(map) {
        const controlContainer = window.document.getElementsByClassName('mapboxgl-control-container')[0];
        if (controlContainer != null) {
          controlContainer.style.opacity = 1;
        }
        if (this.isEditable) {
          map.addControl(this.mapPointControl);
          map.addControl(this.mapPolygonControl);
        }
        map.addControl(this.mapStyleControl);
        map.addControl(this.mapNavigationControl);
        map.addControl(new mapboxgl.ScaleControl());
      },
      hideMapControls(map) {
        const controlContainer = window.document.getElementsByClassName('mapboxgl-control-container')[0];
        if (controlContainer != null) {
          controlContainer.style.opacity = 0;
        }
        map.removeControl(this.mapNavigationControl);
        map.removeControl(this.mapStyleControl);
        if (this.isEditable) {
          map.removeControl(this.mapPolygonControl);
          map.removeControl(this.mapPointControl);
        }
      },
      handlePolygonUpdate(e) {
        const geoJson = e.features[0].geometry;
        const [geofence] = this.geofenceGroup.geofences.filter(
          (x) => `${e.features[0].id}`.localeCompare(`${x.mapboxDrawID}`) === 0,
        );
        if (geofence.isPoint) {
          const geometry = turf.multiPolygon(e.features[0].geometry.coordinates);
          const centroid = turfCentroid(geometry);
          this.updateGeofenceLat({ geofence, value: centroid.coordinates[1] });
          this.updateGeofenceLng({ geofence, value: centroid.coordinates[0] });
        } else {
          this.updateGeofenceGeoJson({ geofence, value: geoJson });
        }
        this.$emit('geofenceUpdated', geofence);
        this.updateGeofenceDataSources();
      },
      updateGeofencePointDataSource() {
        const geofenceCentroidLayerData = this.map.getSource('place_points_source');
        if (geofenceCentroidLayerData === undefined) {
          return;
        }
        const pointFeatures = this.geofenceGroup.geofences.map((x) => x.pointFeature);
        geofenceCentroidLayerData.setData({
          type: 'FeatureCollection',
          features: pointFeatures,
        });
      },
      updateGeofencePolygonDataSource() {
        const geofencePolygonsLayerData = this.map.getSource('place_polygons_source');
        if (geofencePolygonsLayerData === undefined) {
          return;
        }
        const features = this.geofenceGroup.geofences.map((x) => x.feature);
        geofencePolygonsLayerData.setData({
          type: 'FeatureCollection',
          features,
        });

        if (this.areGeofenceBoundingBoxesVisible) {
          const bboxFeatures = this.geofenceGroup.geofences.map((x) => x.boundingBoxFeature);
          this.map.getSource('place_bbox_source').setData({
            type: 'FeatureCollection',
            features: bboxFeatures,
          });
        }
      },
      updateGeofenceDataSources() {
        this.updateGeofencePolygonDataSource();
        this.updateGeofencePointDataSource();
      },
      addGeofence(feature) {
        const ids = this.mapboxDraw.add(feature);
        return ids;
      },
      fitBounds(bounds, padding, duration) {
        this.map.fitBounds(bounds, padding, duration);
      },
      getBounds() {
        return this.map.getBounds();
      },
      zoomToGeofence(geofence) {
        if (this.map.getZoom() > 12) {
          this.map.easeTo({
            center: [geofence.lng, geofence.lat],
          });
        } else {
          const minLat = geofence.boundsSwLat;
          const maxLat = geofence.boundsNeLat;
          const minLng = geofence.boundsSwLng;
          const maxLng = geofence.boundsNeLng;
          const marginV = getContainerHeight('.map-container') / 3;
          const marginH = getContainerWidth('.map-container') / 3;
          this.map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: {
                left: marginH,
                right: marginH,
                top: marginV,
                bottom: marginV,
              },
              duration: 0,
            },
          );
        }
      },
      showClusterPopupAtLocation(lng, lat, inventoryCluster) {
        if (!(this.currentSitePopup === null)) {
          this.currentSitePopup.remove();
        }
        this.currentClusterInventory = inventoryCluster.map((inv) => inv.properties);
        setTimeout(() => {
          const html = window.document.getElementById('inventory_cluster_popup_content').innerHTML;
          this.currentSitePopup = new mapboxgl.Popup({ className: 'site-popup' })
            .setLngLat({ lng, lat })
            .setHTML(html)
            .addTo(this.map);
          const [popup] = window.document.getElementsByClassName('site-popup');
          popup.className = `${popup.className} visible`;
        }, 0); // allow this.currentSite to update
      },
      handlePolygonCreation(e, from3dBuilding = false) {
        if (
          this.mode !== null ||
          (this.mapboxDraw != null &&
            this.mapboxDraw.getSelected() != null &&
            this.mapboxDraw.getSelected().features.length > 0)
        ) {
          return;
        }
        this.mapPolygonControl.deactivate();

        this.$emit('polygonDrawn', e, from3dBuilding);
      },
      handlePolygonDeletion(e) {
        const [geofence] = this.geofenceGroup.geofences.filter(
          (x) => e.features[0].id != null && e.features[0].id.localeCompare(x.mapboxDrawID) === 0,
        );
        this.deleteGeofence(geofence.mapboxDrawID);
      },
      updatePulseMarkerPosition(activeGeofence) {
        if (this.map.getSource('pulse_marker_source') != null) {
          if (activeGeofence != null) {
            this.map.getSource('pulse_marker_source').setData({
              type: 'Point',
              coordinates: [activeGeofence.lng, activeGeofence.lat],
            });
          } else {
            this.map.getSource('pulse_marker_source').setData({
              type: 'Point',
              coordinates: [],
            });
          }
        }
      },
      updateInventoryDataSource() {
        const sitesLayerData = this.map.getSource('inventory_source');
        if (sitesLayerData === undefined || this.unitsInGeofenceGroup == null) {
          return;
        }
        const inventoryFeatures = this.unitsInGeofenceGroup.map((unit) => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [unit.longitude, unit.latitude],
            },
            properties: {
              name: unit.name,
              externalID: unit.externalID,
            },
          };
        });
        sitesLayerData.setData({
          type: 'FeatureCollection',
          features: inventoryFeatures,
        });
        this.superC = new Supercluster({ radius: this.clusterRadius }).load(inventoryFeatures);
      },
      fireClick(clickLocation) {
        this.map.fire('click', clickLocation);
      },
      setCenter(center) {
        this.map.setCenter(center);
      },
      changeMapStyle(uri) {
        this.mapStyle = uri;
        this.map.setStyle(this.mapStyle);
        setTimeout(() => {
          this.createMapLayers();
          this.updateGeofenceDataSources();
          if (this.activeGeofence != null) {
            const g = this.activeGeofence;
            this.$emit('pointClicked', g);
          }
        }, 1000);
      },
      changeMapStyleToSatellite() {
        this.changeMapStyle('mapbox://styles/mapbox/satellite-streets-v9');
      },
      changeMapStyleToLight() {
        this.changeMapStyle('mapbox://styles/mapbox/light-v9');
      },
      startSlideShow() {
        this.hideMapControls(this.map);
        this.slideShowGeofenceIndex = 0;
        this.map.setPitch(60);
        this.jumpAndRotateAroundNextGeofence();
      },
      jumpAndRotateAroundNextGeofence() {
        const jumpDuration = 2000;
        this.activeSlideShowGeofence = null;
        const geofence = this.geofenceGroup.geofences[this.slideShowGeofenceIndex];
        this.map.easeTo({
          bearing: 0,
          center: [geofence.lng, geofence.lat],
          zoom: 19,
          duration: jumpDuration,
        });

        this.slideShowRotationDelayTimer = setTimeout(() => {
          setTimeout(() => {
            this.activeSlideShowGeofence = geofence;
          }, 500);
          this.slideShowRotationTimer = setTimeout(() => {
            this.map.easeTo({
              bearing: this.map.getBearing() + 45,
              duration: 10000,
              zoom: 15,
              easing: (t) => t,
            });
          }, 1000);
        }, jumpDuration - 500);

        this.slideShowGeofenceIndex += 1;
        if (this.slideShowGeofenceIndex === this.geofenceGroup.geofences.length) {
          this.slideShowGeofenceIndex = 0;
        }

        this.slideShowFlyBetweenGeofencesTimer = setTimeout(() => {
          this.jumpAndRotateAroundNextGeofence();
        }, 11000);
      },
      stopSlideShow() {
        this.map.stop();
        this.showMapControls(this.map);

        if (this.slideShowFlyBetweenGeofencesTimer !== null) {
          clearTimeout(this.slideShowFlyBetweenGeofencesTimer);
          this.slideShowFlyBetweenGeofencesTimer = null;
        }
        if (this.slideShowRotationDelayTimer !== null) {
          clearTimeout(this.slideShowRotationDelayTimer);
          this.slideShowRotationDelayTimer = null;
        }
        if (this.slideShowRotationTimer !== null) {
          clearTimeout(this.slideShowRotationTimer);
          this.slideShowRotationTimer = null;
        }

        this.map.stop();
      },
    },
    mounted() {
      // The container for mapOptions cannot be found when there is no delay.
      setTimeout(this.initMap, 100);
    },
    unmounted() {
      this.unitClustersService?.$destroy();
    },
  };
</script>
<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  .site-context-menu {
    .mapboxgl-popup-content {
      background-color: #fff;
      padding: 1rem !important;
    }
  }

  .site-popup-content {
    background-color: #fff;
    display: flex;
    align-items: center;
    max-width: 40rem;
    max-width: 40vw;
    font-size: 1.2rem;

    svg {
      min-width: 3.2rem;
      margin-right: 1.2rem;
    }

    .unit-name {
      display: flex;
      flex-direction: column;
      line-height: initial;

      strong {
        font-size: 1.2em;
        margin-bottom: 0.6rem;
      }
    }

    .inventory-table-container {
      overflow-y: auto;
      max-height: 15rem;
      height: auto;
      min-width: 20rem;

      table {
        margin: 0;
        width: 100%;
        thead {
          th {
            padding: 0.1rem 1rem 0.1rem 0;
          }
        }

        tbody {
          td {
            font-weight: 400;
            font-size: 0.9em;
            padding: 0.1rem 1rem 0.1rem 0;
          }
        }
      }
    }
  }
</style>
